import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Lock, Mail, Shield, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Deep links (e.g. /user/connections from an email) come back here after sign-in
    const fromPath = location.state?.from?.pathname;
    const returnTo = fromPath && fromPath.startsWith('/user/') && fromPath !== '/user/change-password'
        ? `${fromPath}${location.state.from.search || ''}`
        : '/user/dashboard';
    const { login } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        totpToken: ''
    });
    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [errors, setErrors] = useState({});

    // Login mutation - Updated to use authStore login method
    const loginMutation = useMutation({
        mutationFn: async (data) => {
            // Use the login method from authStore instead of calling API directly
            const result = await login(data.email, data.password, data.totpToken || null);
            return result;
        },
        onSuccess: (data) => {
            if (data.requiresTwoFactor) {
                setShowTwoFactor(true);
                setErrors({});
            } else if (data.success) {
                // The authStore already handles setting the auth state
                // Just handle the redirect here based on mustChangePassword
                if (data.mustChangePassword) {
                    navigate('/user/change-password');
                } else {
                    navigate(returnTo, { replace: true });
                }
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message || error.message || 'Invalid email or password';
            const remainingAttempts = error.response?.data?.remainingAttempts;

            if (remainingAttempts !== undefined) {
                setErrors({ general: `${message} (${remainingAttempts} attempts remaining)` });
            } else {
                setErrors({ general: message });
            }
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear errors when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleTwoFactorChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, totpToken: value }));
        if (errors.twoFactor) {
            setErrors(prev => ({ ...prev, twoFactor: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            loginMutation.mutate(formData);
        }
    };

    const handleTwoFactorSubmit = (e) => {
        e.preventDefault();
        if (!formData.totpToken) {
            setErrors({ twoFactor: 'Verification code is required' });
            return;
        }
        // Re-submit the login request with the TOTP token
        loginMutation.mutate(formData);
    };

    if (showTwoFactor) {
        return (
            <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-hx-bg py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md bg-hx-panel border border-hx-line rounded-2xl p-6 sm:p-8 shadow-2xl w-full space-y-8">
                    <div>
                        <div className="flex justify-center">
                            <div className="bg-hx-accent/10 p-3 rounded-full">
                                <Shield className="h-12 w-12 text-hx-accent" />
                            </div>
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-hx-text">
                            Two-Factor Authentication
                        </h2>
                        <p className="mt-2 text-center text-sm text-hx-muted">
                            Enter the verification code from your authenticator app
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleTwoFactorSubmit}>
                        <div>
                            <label htmlFor="totpToken" className="block text-sm font-medium text-hx-muted mb-1">
                                Verification Code
                            </label>
                            <input
                                id="totpToken"
                                name="totpToken"
                                type="text"
                                autoComplete="one-time-code"
                                required
                                maxLength="6"
                                className={`appearance-none bg-hx-bg2 relative block w-full px-3 py-2 border ${
                                    errors.twoFactor ? 'border-red-500/60' : 'border-hx-line2'
                                } placeholder-hx-dim text-hx-text rounded-md focus:outline-none focus:ring-hx-accent focus:border-hx-accent focus:z-10 sm:text-sm`}
                                placeholder="Enter 6-digit code"
                                value={formData.totpToken}
                                onChange={handleTwoFactorChange}
                                autoFocus
                            />
                            {errors.twoFactor && (
                                <p className="mt-1 text-sm text-red-400">{errors.twoFactor}</p>
                            )}
                        </div>

                        {errors.general && (
                            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-4">
                                <p className="text-sm text-red-300">{errors.general}</p>
                            </div>
                        )}

                        <div>
                            <button
                                type="submit"
                                disabled={loginMutation.isPending}
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-hx-ink bg-hx-accent hover:bg-hx-accent2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-hx-panel focus:ring-hx-accent disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loginMutation.isPending ? (
                                    <>
                                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                        Verifying...
                                    </>
                                ) : (
                                    'Verify'
                                )}
                            </button>
                        </div>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowTwoFactor(false);
                                    setFormData(prev => ({ ...prev, totpToken: '' }));
                                    setErrors({});
                                }}
                                className="text-sm text-hx-accent hover:text-hx-accent2"
                            >
                                Back to login
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-hx-bg py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md bg-hx-panel border border-hx-line rounded-2xl p-6 sm:p-8 shadow-2xl w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-hx-text">
                        Sign in to your account
                    </h2>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {errors.general && (
                        <div className="rounded-md bg-red-500/10 border border-red-500/30 p-4">
                            <p className="text-sm text-red-300">{errors.general}</p>
                        </div>
                    )}

                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="email-address" className="sr-only">
                                Email address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20">
                                    <Mail className={`h-5 w-5 ${errors.email ? 'text-red-400' : 'text-hx-dim'}`} />
                                </div>
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className={`appearance-none bg-hx-bg2 rounded-none relative block w-full pl-10 px-3 py-2 border ${
                                        errors.email ? 'border-red-500/60' : 'border-hx-line2'
                                    } placeholder-hx-dim text-hx-text rounded-t-md focus:outline-none focus:ring-hx-accent focus:border-hx-accent focus:z-10 sm:text-sm`}
                                    placeholder="Email address"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">
                                Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20">
                                    <Lock className={`h-5 w-5 ${errors.password ? 'text-red-400' : 'text-hx-dim'}`} />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    required
                                    className={`appearance-none bg-hx-bg2 rounded-none relative block w-full pl-10 pr-10 px-3 py-2 border ${
                                        errors.password ? 'border-red-500/60' : 'border-hx-line2'
                                    } placeholder-hx-dim text-hx-text rounded-b-md focus:outline-none focus:ring-hx-accent focus:border-hx-accent focus:z-10 sm:text-sm`}
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-hx-dim hover:text-hx-muted" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-hx-dim hover:text-hx-muted" />
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div className="text-sm">
                            <Link to="/forgot-password" className="font-medium text-hx-accent hover:text-hx-accent2">
                                Forgot your password?
                            </Link>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loginMutation.isPending}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-hx-ink bg-hx-accent hover:bg-hx-accent2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-hx-panel focus:ring-hx-accent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loginMutation.isPending ? (
                                <>
                                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                        <Lock className="h-5 w-5 text-hx-ink/70 group-hover:text-hx-ink" />
                                    </span>
                                    Sign in
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;